import { CancelFunc, emptyCancel, wrapDispatch } from 'lib/cancel'
import { convertError, EditReturn, Status } from 'lib/rpc'
import { ActionStatus, expandValue, expandValueList, unaryInvoke } from 'lib/rpcts'
import { AppEntitlement } from 'pbts/c1/api/app/v1/app_entitlement'
import { AppEntitlementRef } from 'pbts/c1/api/app/v1/app_refs'
import { RequestCatalog, RequestCatalogView } from 'pbts/c1/api/requestcatalog/v1/requestcatalog'
import {
    RequestCatalogManagementServiceAddAppEntitlementsRequest,
    RequestCatalogManagementServiceCreateRequest,
    RequestCatalogManagementServiceGetResponse,
    RequestCatalogManagementServiceRemoveAppEntitlementsRequest,
} from 'pbts/c1/api/requestcatalog/v1/requestcatalogmanagement'
import { RequestCatalogManagementServiceClient } from 'pbts/c1/api/requestcatalog/v1/requestcatalogmanagement.client'
import { User } from 'pbts/c1/api/user/v1/user'
import { Any } from 'pbts/google/protobuf/any'
import { Dispatch } from 'react'

export enum RequestCatalogAction {
    create = 'RequestCatalogAction.create',
    editAppEntitlements = 'RequestCatalogAction.editAppEntitlements',
}

export interface RequestCatalogValue extends RequestCatalog {
    createdByUser?: User
    accessEntitlements: AppEntitlement[]
    memberCount?: number
}

export interface RequestCatalogViewResponse {
    requestCatalogView?: RequestCatalogView
    expanded: Any[]
}

export const expandRequestCatalogPayload = (
    payload: ActionStatus<RequestCatalogViewResponse>,
): ActionStatus<RequestCatalogValue> => {
    switch (payload.status) {
        case Status.OK:
            return {
                status: Status.OK,
                item: expandRequestCatalogView(payload.item.requestCatalogView, payload.item.expanded),
            }
        default:
            return payload
    }
}

export type EditAppEntitlements = {
    type: RequestCatalogAction.editAppEntitlements
    payload: EditReturn
}

export type CreateAction = {
    type: RequestCatalogAction.create
    payload: ActionStatus<RequestCatalogValue>
}

export type RequestCatalogActions =
    | CreateAction
    | EditAppEntitlements

export const expandRequestCatalogView = (msg: RequestCatalogView | undefined, values: Any[]): RequestCatalogValue => {
    const createdByUser = expandValue<User>(msg?.createdByUserPath || '', User, values)
    const accessEntitlements =
        expandValueList<AppEntitlement>(msg?.accessEntitlementsPath || '', AppEntitlement, values) || []
    const memberCount = msg?.memberCount

    return {
        ...RequestCatalog.create(msg?.requestCatalog ?? {}),
        createdByUser,
        accessEntitlements,
        memberCount,
    }
}

export const createRequestCatalog = (
    dispatch: Dispatch<CreateAction>,
    client: RequestCatalogManagementServiceClient | undefined,
    displayName: string,
    description: string,
): CancelFunc => {
    if (!client) {
        return emptyCancel
    }
    const req = RequestCatalogManagementServiceCreateRequest.create({
        displayName,
        description,
        expandMask: { paths: ['*'] },
        visibleToEveryone: true,
    })
    return unaryInvoke<RequestCatalogManagementServiceCreateRequest, RequestCatalogManagementServiceGetResponse>(
        req,
        client.create.bind(client),
        (payload) => {
            dispatch({
                type: RequestCatalogAction.create,
                payload: expandRequestCatalogPayload(payload),
            })
        },
    )
}

export const editAppEntitlements = (
    dispatcher: Dispatch<EditAppEntitlements>,
    client: RequestCatalogManagementServiceClient | undefined,
    requestCatalogId: string,
    toAdd: AppEntitlementRef[],
    toRemove: AppEntitlementRef[],
    createRequests?: boolean,
): (() => void) | undefined => {
    const [dispatch, cancel] = wrapDispatch(dispatcher)
    if (!client) {
        return
    }

    dispatch({ type: RequestCatalogAction.editAppEntitlements, payload: { status: Status.LOADING } })

    const doEditAppEntitlements = async (): Promise<void> => {
        try {
            if (toAdd && toAdd.length > 0) {
                const addReq = RequestCatalogManagementServiceAddAppEntitlementsRequest.create({
                    catalogId: requestCatalogId,
                    appEntitlements: toAdd,
                    createRequests,
                })

                await client.addAppEntitlements(addReq)
            }

            if (toRemove && toRemove.length > 0) {
                const removeReq = RequestCatalogManagementServiceRemoveAppEntitlementsRequest.create({
                    catalogId: requestCatalogId,
                    appEntitlements: toRemove,
                })

                await client.removeAppEntitlements(removeReq)
            }
        } catch (err) {
            dispatch({
                type: RequestCatalogAction.editAppEntitlements,
                payload: { status: Status.ERROR, error: convertError(err) },
            })
            return
        }
        dispatch({ type: RequestCatalogAction.editAppEntitlements, payload: { status: Status.OK } })
    }

    doEditAppEntitlements()

    return (): void => {
        cancel()
    }
}
